<template>
  <b-nav-item-dropdown
    class="dropdown-notification"
    menu-class="dropdown-menu-media"
    right
    @show="loadNotifications"
  >
    <template #button-content>
      <feather-icon
        :badge="badge"
        badge-classes="bg-danger"
        class="text-light"
        icon="BellIcon"
        size="21"
      />
    </template>

    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex align-items-center">
        <h4 class="notification-title mb-0 mr-auto">
          Notificaciones
        </h4>
        <b-badge
          v-if="unreadCount"
          pill
          variant="light-primary"
        >
          {{ unreadCount }} sin leer
        </b-badge>
      </div>
    </li>

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <div
        v-if="loading && !notifications.length"
        class="text-center text-muted py-2"
      >
        Cargando…
      </div>

      <div
        v-else-if="!notifications.length"
        class="text-center text-muted py-2 px-1"
      >
        No tienes notificaciones
      </div>

      <b-link
        v-for="item in notifications"
        :key="item.id"
        @click="open(item)"
      >
        <b-media :class="{ 'notification-unread': !item.is_read }">
          <template #aside>
            <b-avatar
              size="32"
              :variant="item.is_read ? 'light-secondary' : 'light-primary'"
            >
              <feather-icon icon="BellIcon" />
            </b-avatar>
          </template>
          <p class="media-heading mb-0">
            <span class="font-weight-bolder">
              {{ item.notification && item.notification.title }}
            </span>
          </p>
          <small class="notification-text d-block">
            {{ preview(item) }}
          </small>
          <small class="text-muted">{{ formatDate(item.created_at) }}</small>
        </b-media>
      </b-link>
    </vue-perfect-scrollbar>

    <li
      v-if="unreadCount"
      class="dropdown-menu-footer"
    >
      <b-button
        variant="primary"
        block
        @click="markAllRead"
      >
        Marcar todas como leídas
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { isUserLoggedIn } from '@/auth/utils'

const REFRESH_MS = 120000

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    BButton,
    VuePerfectScrollbar,
  },

  data() {
    return {
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
      timer: null,
    }
  },

  computed: {
    ...mapGetters('userNotifications', {
      notifications: 'userNotifications',
      unreadCount: 'userNotificationsUnreadCount',
      loading: 'userNotificationsLoading',
    }),

    badge() {
      if (!this.unreadCount) return null
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount)
    },
  },

  created() {
    this.loadNotifications()
    this.timer = setInterval(this.loadNotifications, REFRESH_MS)
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },

  methods: {
    loadNotifications() {
      // Sin sesion la peticion responde 401, y ese 401 se interpreta como sesion muerta.
      if (!isUserLoggedIn()) return Promise.resolve()

      return this.$store.dispatch('userNotifications/fetchUserNotifications')
    },

    // content_message trae HTML de las plantillas de correo; en la campana solo
    // queremos el texto, sin inyectar markup ajeno en el DOM.
    preview(item) {
      const raw = (item.notification && item.notification.content_message) || ''
      const text = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      return text.length > 120 ? `${text.slice(0, 120)}…` : text
    },

    formatDate(value) {
      if (!value) return ''
      return moment(value).format('DD/MMM/YYYY HH:mm')
    },

    async open(item) {
      if (!item.is_read) {
        await this.$store.dispatch('userNotifications/markUserNotificationRead', item.id)
      }

      const link = item.notification && item.notification.link_action
      if (link) window.location.assign(link)
    },

    markAllRead() {
      return this.$store.dispatch('userNotifications/markAllUserNotificationsRead')
    },
  },
}
</script>

<style scoped>
.notification-unread {
  background-color: rgba(115, 103, 240, 0.08);
}

.notification-text {
  white-space: normal;
}
</style>
